/**
 * Catchment overlay for the property-detail map.
 *
 * Mounts inside a react-leaflet <MapContainer> and paints either:
 *   • US official NCES school district polygon, OR
 *   • Voronoi cells around each nearby school (IE/UK approximation)
 *
 * Fetches on mount, hides itself if the endpoint returns nothing.
 * A parent must pass `active` (bool) — when false we render nothing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { GeoJSON, Polygon, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Delaunay } from 'd3-delaunay';

const _cache = new Map();
const _paletteFor = (i) => [
  '#7042cc', '#0284c7', '#16a34a', '#f59e0b', '#dc2626',
  '#0891b2', '#c026d3', '#84cc16', '#e11d48', '#0d9488',
][i % 10];

// Small pill icon for the school marker.
const _schoolIcon = (colour) => L.divIcon({
  className: 'lh-catchment-pin',
  html: `<div style="width:12px;height:12px;border-radius:3px;background:${colour};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>`,
  iconSize:  [12, 12],
  iconAnchor:[6, 6],
});

// Clip the Voronoi diagram to a generous bounding box around the
// property so cells don't stretch to infinity.
const _computeVoronoi = (schools, centerLat, centerLng) => {
  if (!schools || schools.length < 2) return [];
  // ~5 km bbox
  const halfLat = 0.045, halfLng = 0.07;
  const bbox = [
    centerLng - halfLng, centerLat - halfLat,
    centerLng + halfLng, centerLat + halfLat,
  ];
  const pts = schools.map((s) => [s.lng, s.lat]);
  const del = Delaunay.from(pts);
  const vor = del.voronoi(bbox);
  return schools.map((s, i) => {
    const cell = vor.cellPolygon(i);
    if (!cell) return null;
    // GeoJSON expects [lng, lat]; react-leaflet <Polygon> expects [lat, lng].
    const positions = cell.map(([x, y]) => [y, x]);
    return { school: s, positions, colour: _paletteFor(i) };
  }).filter(Boolean);
};

export const CatchmentOverlay = ({ lat, lng, country, active, onData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const map = useMap();

  useEffect(() => {
    if (!active || lat == null || lng == null) return;
    const key = `${Number(lat).toFixed(3)},${Number(lng).toFixed(3)},${country || ''}`;
    if (_cache.has(key)) {
      const cached = _cache.get(key);
      setData(cached);
      if (onData) onData(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catchment?lat=${lat}&lng=${lng}&country=${encodeURIComponent(country || '')}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled) return;
        if (d) {
          _cache.set(key, d);
          setData(d);
          if (onData) onData(d);
        }
      })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, lat, lng, country, onData]);

  const voronoi = useMemo(
    () => (data?.mode === 'approx_voronoi' ? _computeVoronoi(data.schools || [], lat, lng) : []),
    [data, lat, lng],
  );

  if (!active) return null;
  if (!data && loading) return null;
  if (!data) return null;

  // US: official district polygon.
  if (data.mode === 'us_official' && data.polygon) {
    return (
      <GeoJSON
        key={`${data.district?.lea_id || 'us'}`}
        data={data.polygon}
        style={{
          color:      '#7042cc',
          weight:     2,
          dashArray:  '4 4',
          fillColor:  '#7042cc',
          fillOpacity: 0.10,
        }}
        onEachFeature={(_, layer) => {
          if (data.district?.name) {
            layer.bindTooltip(
              `<div style="font-weight:600;font-size:11px">${data.district.name}</div><div style="font-size:10px;color:#666">Official US school district</div>`,
              { sticky: true, direction: 'top' },
            );
          }
        }}
      />
    );
  }

  // IE / UK: Voronoi approximation + a floating disclaimer badge.
  return (
    <>
      {voronoi.map((cell) => (
        <React.Fragment key={cell.school.id}>
          <Polygon
            positions={cell.positions}
            pathOptions={{
              color:       cell.colour,
              weight:      1.5,
              fillColor:   cell.colour,
              fillOpacity: 0.15,
              dashArray:   '3 3',
            }}
          />
          <Marker position={[cell.school.lat, cell.school.lng]} icon={_schoolIcon(cell.colour)}>
            <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
              <div className="text-xs">
                <div className="font-semibold">{cell.school.name}</div>
                <div className="text-gray-500 text-[10px]">
                  {[cell.school.level, cell.school.operator, cell.school.gender].filter(Boolean).join(' · ')}
                </div>
              </div>
            </Tooltip>
          </Marker>
        </React.Fragment>
      ))}
    </>
  );
};

// A separate DOM overlay (rendered outside MapContainer) that shows
// the "estimated / official" disclaimer as a badge on the map — this
// component is a lightweight sibling used by PropertyMap for framing.
export const CatchmentDisclaimer = ({ data, active }) => {
  if (!active || !data) return null;
  if (data.mode === 'us_official') {
    return (
      <div className="absolute top-2 right-2 z-[500] bg-white/95 backdrop-blur rounded-lg shadow-md ring-1 ring-emerald-200 px-2.5 py-1.5 text-[10px] max-w-[220px]"
             data-testid="catchment-disclaimer-us">
        <div className="font-bold text-emerald-700 uppercase tracking-wider">Official school catchment area</div>
        <div className="text-gray-600 mt-0.5">
          NCES school district: <span className="font-semibold">{data.district?.name || '—'}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="absolute top-2 right-2 z-[500] bg-white/95 backdrop-blur rounded-lg shadow-md ring-1 ring-amber-200 px-2.5 py-1.5 text-[10px] max-w-[240px]"
           data-testid="catchment-disclaimer-approx">
      <div className="font-bold text-amber-700 uppercase tracking-wider">Estimated school catchment area</div>
      <div className="text-gray-600 mt-0.5 leading-tight">
        Official catchment areas aren&apos;t published here. Coloured cells show the
        <em> nearest school</em> only — always check each school&apos;s enrolment policy.
      </div>
    </div>
  );
};

export default CatchmentOverlay;
